// The schema walker: a form rendered from a configurator's zod schema
// (@aiw/config). Strings → inputs/textareas, numbers → number inputs, enums →
// selects, booleans → checkboxes, arrays of objects → repeatable rows,
// records → key/value rows. It reads the schema's own definition object
// (zod 4: `schema._zod.def`) structurally, so the CRM needs no zod of its own
// and a new kind renders the moment it is registered.
import { useId } from "react";
import { Button, Input, Textarea, cn } from "@aiw/ui";
import { Plus, Trash2 } from "lucide-react";

// ── the slice of a zod 4 schema this walker reads ──
interface Check { _zod: { def: { check: string; minimum?: number; maximum?: number; value?: number; inclusive?: boolean } } }
interface Def {
  type: string;
  shape?: Record<string, Schema>;
  element?: Schema;
  entries?: Record<string, string | number>;
  innerType?: Schema;
  defaultValue?: unknown;
  keyType?: Schema;
  valueType?: Schema;
  checks?: Check[];
  format?: string;
}
export interface Schema { _zod: { def: Def } }

const def = (s: Schema): Def => s._zod.def;
const WRAPPERS = new Set(["optional", "nullable", "default", "nonoptional", "readonly", "catch"]);

/** Strip optional/default/nullable wrappers; say whether the field may be left out. */
function unwrap(s: Schema): { inner: Schema; optional: boolean; nullable: boolean } {
  let cur = s, optional = false, nullable = false;
  while (WRAPPERS.has(def(cur).type) && def(cur).innerType) {
    const t = def(cur).type;
    if (t === "optional" || t === "default") optional = true;
    if (t === "nullable") nullable = true;
    cur = def(cur).innerType!;
  }
  return { inner: cur, optional, nullable };
}

/** A value that satisfies the schema's shape, for a new row or a new key. */
export function emptyFor(s: Schema): unknown {
  const d = def(s);
  if (d.type === "default") return d.defaultValue;
  if (d.type === "nullable") return null;
  if (WRAPPERS.has(d.type) && d.innerType) return emptyFor(d.innerType);
  switch (d.type) {
    case "object": return Object.fromEntries(Object.entries(d.shape ?? {}).map(([k, v]) => [k, emptyFor(v)]));
    case "string": return "";
    case "number": case "int": return 0;
    case "boolean": return false;
    case "enum": return Object.values(d.entries ?? {})[0] ?? "";
    case "array": return [];
    case "record": return {};
    default: return null;
  }
}

const words = (k: string) => k.replace(/_/g, " ");
const numberBounds = (d: Def) => {
  let min: number | undefined, max: number | undefined;
  for (const c of d.checks ?? []) {
    const cd = c._zod.def;
    if (cd.check === "greater_than") min = cd.value;
    if (cd.check === "less_than") max = cd.value;
  }
  return { min, max };
};
const minLength = (d: Def) => (d.checks ?? []).find((c) => c._zod.def.check === "min_length")?._zod.def.minimum ?? 0;

const selectCls = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export interface FieldProps { schema: Schema; value: unknown; onChange: (v: unknown) => void; name?: string; depth?: number }

/** One field of any shape. Objects render their keys; everything else is a control. */
export function Field({ schema, value, onChange, name = "", depth = 0 }: FieldProps) {
  const { inner, optional } = unwrap(schema);
  const d = def(inner);
  const id = useId();
  const label = name ? words(name) : "";

  switch (d.type) {
    case "object":
      return (
        <fieldset className={cn(depth > 0 && "rounded-md border bg-background p-3")}>
          {label && <legend className="mb-1 px-1 text-sm font-medium">{label}</legend>}
          <div className={cn("grid gap-3", depth === 0 ? "sm:grid-cols-2" : "sm:grid-cols-2")}>
            {Object.entries(d.shape ?? {}).map(([k, s]) => {
              const inner2 = unwrap(s).inner;
              const wide = ["object", "array", "record"].includes(def(inner2).type) || (def(inner2).type === "string" && minLength(def(inner2)) >= 20);
              const obj = (value ?? {}) as Record<string, unknown>;
              return (
                <div key={k} className={cn(wide && "sm:col-span-2")}>
                  <Field schema={s} value={obj[k]} onChange={(v) => onChange({ ...obj, [k]: v })} name={k} depth={depth + 1} />
                </div>
              );
            })}
          </div>
        </fieldset>
      );

    case "array": {
      const el = d.element!;
      const elInner = unwrap(el).inner;
      const list = Array.isArray(value) ? (value as unknown[]) : [];
      if (def(elInner).type === "enum") {
        const opts = Object.values(def(elInner).entries ?? {}).map(String);
        return (
          <Labelled label={label} hint="tick what applies">
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {opts.map((o) => (
                <label key={o} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={list.includes(o)} onChange={(e) => onChange(e.target.checked ? [...list, o] : list.filter((x) => x !== o))} />
                  {words(o)}
                </label>
              ))}
            </div>
          </Labelled>
        );
      }
      return (
        <Labelled label={label} hint={`${list.length} row${list.length === 1 ? "" : "s"}`}>
          <div className="space-y-2">
            {list.map((row, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Field schema={el} value={row} onChange={(v) => onChange(list.map((r, j) => (j === i ? v : r)))} depth={depth + 1} />
                </div>
                <Button type="button" size="sm" variant="ghost" aria-label="Remove row" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => onChange([...list, emptyFor(el)])}><Plus /> Add {label ? words(label).replace(/s$/, "") : "row"}</Button>
          </div>
        </Labelled>
      );
    }

    case "record": {
      const rec = (value ?? {}) as Record<string, unknown>;
      const keys = Object.keys(rec);
      const vs = d.valueType!;
      const simple = ["string", "number", "int", "boolean", "enum"].includes(def(unwrap(vs).inner).type);
      const rename = (from: string, to: string) => {
        if (to === from) return;
        onChange(Object.fromEntries(Object.entries(rec).map(([k, v]) => [k === from ? to : k, v])));
      };
      const add = () => {
        let k = "new_key", n = 1;
        while (k in rec) k = `new_key_${++n}`;
        onChange({ ...rec, [k]: emptyFor(vs) });
      };
      return (
        <Labelled label={label} hint={`${keys.length} ${keys.length === 1 ? "entry" : "entries"} · key → value`}>
          <div className="space-y-2">
            {keys.map((k) => (
              <div key={k} className={cn("gap-2", simple ? "grid grid-cols-[minmax(0,12rem)_1fr_auto] items-center" : "rounded-md border bg-background p-3")}>
                <div className={cn(!simple && "mb-2 flex items-center gap-2")}>
                  <Input defaultValue={k} aria-label="Key" className="font-mono text-micro" onBlur={(e) => rename(k, e.target.value.trim() || k)} />
                  {!simple && <Button type="button" size="sm" variant="ghost" aria-label="Remove entry" onClick={() => { const n = { ...rec }; delete n[k]; onChange(n); }}><Trash2 /></Button>}
                </div>
                <Field schema={vs} value={rec[k]} onChange={(v) => onChange({ ...rec, [k]: v })} depth={depth + 1} />
                {simple && <Button type="button" size="sm" variant="ghost" aria-label="Remove entry" onClick={() => { const n = { ...rec }; delete n[k]; onChange(n); }}><Trash2 /></Button>}
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={add}><Plus /> Add entry</Button>
          </div>
        </Labelled>
      );
    }

    case "enum": {
      const opts = Object.values(d.entries ?? {}).map(String);
      return (
        <Labelled label={label} htmlFor={id}>
          <select id={id} className={selectCls} value={value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value)}>
            {optional && <option value="">—</option>}
            {opts.map((o) => <option key={o} value={o}>{words(o)}</option>)}
          </select>
        </Labelled>
      );
    }

    case "boolean":
      return (
        <label className="flex min-h-9 items-center gap-2 text-sm">
          <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
      );

    case "number":
    case "int": {
      const { min, max } = numberBounds(d);
      return (
        <Labelled label={label} htmlFor={id} hint={min != null || max != null ? `${min != null ? `min ${min}` : ""}${min != null && max != null ? " · " : ""}${max != null ? `max ${max}` : ""}` : undefined}>
          <Input id={id} type="number" step="any" min={min} max={max} className="num" value={value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? (optional ? undefined : 0) : Number(e.target.value))} />
        </Labelled>
      );
    }

    case "string": {
      const str = value == null ? "" : String(value);
      const long = minLength(d) >= 20 || str.length > 60 || /body|text|message|html/.test(name);
      const set = (v: string) => onChange(v === "" && optional ? undefined : v);
      return (
        <Labelled label={label} htmlFor={id} hint={optional ? "optional" : undefined}>
          {long
            ? <Textarea id={id} rows={3} value={str} onChange={(e) => set(e.target.value)} />
            : <Input id={id} type={d.format === "email" ? "email" : "text"} value={str} onChange={(e) => set(e.target.value)} />}
        </Labelled>
      );
    }

    default:
      return (
        <Labelled label={label} hint="as data">
          <Textarea rows={3} className="font-mono text-micro" defaultValue={JSON.stringify(value ?? null, null, 1)} onBlur={(e) => { try { onChange(JSON.parse(e.target.value)); } catch { /* leave the last good value */ } }} />
        </Labelled>
      );
  }
}

function Labelled({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1">
      {(label || hint) && (
        <div className="flex items-baseline justify-between gap-2">
          {label ? <label htmlFor={htmlFor} className="text-sm font-medium">{label}</label> : <span />}
          {hint && <span className="text-micro text-muted-foreground">{hint}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

/** The whole document: the kind's schema at depth 0. */
export function SettingsForm({ schema, value, onChange }: { schema: Schema; value: unknown; onChange: (v: unknown) => void }) {
  return <Field schema={schema} value={value} onChange={onChange} />;
}
